import { DripsyFinalTheme, makeTheme } from 'dripsy';
import { merge } from 'lodash';
import { baseTheme } from '../../design-system/baseTheme/baseTheme';
import { palette } from './palette';

const baseColors = {
  $primary: palette.P2.$800,
  $secondary: palette.P1.$700,
  $accent: palette.P2.$300,
  // Text
  $textPrimary: palette.Black.$1000,
  $textSecondary: palette.Black.$800,
  $textDisable: palette.Black.$700,
  $textButtonPrimary: palette.White.$700,
  $textButtonSecondary: palette.Black.$1000,
  $textButtonOutline: palette.P2.$900,
  $textTooltip: palette.White.$1000,
  $textDateDisabled: palette.Black.$600,
  $textAlertDefault: palette.Black.$1000,
  $textAlertContrast: palette.Black.$1000,
  $textTitle: palette.P2.$500,
  $textDarkBackground: palette.White.$700,
  // Icon
  $iconDefault: palette.Black.$800,
  $iconFocused: palette.P1.$300,
  // Border
  $borderPrimary: palette.Black.$400,
  $borderButton: palette.Black.$600, // #CCCCCC
  $borderPageItem: palette.Black.$300, // #F0F0F0
  $borderDivider: palette.Black.$500, // #D8D8D8
  // Background
  $backgroundPrimary: palette.White.$700,
  $backgroundSecondary: palette.Dark.$100,
  $backgroundInputActive: palette.P1.$700,
  $backgroundPopup: palette.White.$700,
  // Line Separator
  $lineThin: palette.Black.$400,
  $lineThick: palette.Dark.$100,
  // Status
  $success: palette.Green.$700,
  $danger: palette.Red.$700,
  $warning: palette.Background.YELLOW_BG,
  $info: palette.Background.BLUE_BG,
  $disable: palette.Black.$300,
  // OTHERS
  $hyperlink: palette.P2.$800,
  // Pie Chart
  $pieChart0: palette.P1.$1000,
  $pieChart1: palette.P1.$800,
  $pieChart2: palette.P1.$700,
  $pieChart3: palette.P1.$600,
  // Skeleton
  $skeleton: palette.Black.$600,
  // Alerts
  $alertDanger: palette.Red.$100,
  $alertWarning: '#FFF7D4',
  // Date Picker
  $datePickerSelected: palette.P2.$800,
  $datePickerSelectedText: palette.White.$700,
  $datePickerHighlightedBackground: palette.P2.$400,
  $datePickerHighlightedText: palette.Black.$1000,
  $datePickerDisabledText: palette.Black.$600,
  $datePickerText: palette.Black.$1000
};

const baseLinearGradients = {
  primaryDefault: [palette.P2.$800, palette.P2.$600],
  primaryPressed: [palette.P2.$700, '#6B5DE6'],
  secondary1Default: [palette.P2.$300, palette.P2.$300],
  secondary1Pressed: [palette.P2.$400, palette.P2.$400],
  secondary2Default: [palette.Black.$300, palette.Black.$300],
  secondary2Pressed: [palette.P2.$400, palette.P2.$400],
  outlineDefault: ['transparent', 'transparent'],
  outlinePressed: ['transparent', 'transparent'],
  disabledDefault: [palette.Black.$300, palette.Black.$300],
  disabledPressed: [palette.Black.$300, palette.Black.$300],
  menuUnderline: ['#FFFFFF80', 'transparent'],
  menuUnderlineDark: ['#00000060', 'transparent'],
  // LEGACY
  primaryButtonDefault: [palette.P2.$800, palette.P2.$700],
  primaryButtonPressed: [palette.P2.$500, palette.P2.$400],
  secondary1ButtonDefault: [palette.P2.$400, palette.P2.$400],
  secondary1ButtonPressed: [palette.P2.$300, palette.P2.$300],
  secondary2ButtonDefault: [palette.Black.$300, palette.Black.$300],
  secondary2ButtonPressed: [palette.P2.$400, palette.P2.$400],
  outlineButtonDefault: ['transparent', 'transparent'],
  outlineButtonPressed: ['transparent', 'transparent'],
  buttonDisabled: [palette.Black.$300, palette.Black.$300]
};

const base = {
  ...baseTheme,
  linearGradients: baseLinearGradients
};

const colors = {
  light: baseColors,
  dark: {
    ...baseColors,
    $primary: palette.P2.$600,
    $accent: palette.P2.$1000,
    // Text
    $textPrimary: palette.White.$700,
    $textSecondary: palette.Dark.$400,
    $textDisable: palette.Black.$600,
    $textButtonOutline: palette.P2.$500,
    $textAlertDefault: palette.White.$700,
    $textAlertContrast: palette.Black.$1000,
    $textDarkBackground: palette.White.$700,
    // Icon
    $iconDefault: palette.Black.$800,
    $iconFocused: palette.P1.$300,
    // Border
    $borderPrimary: palette.Dark.$700,
    $borderPageItem: palette.Black.$900, // #333333
    $borderDivider: palette.Dark.$700, // #D8D8D8
    $borderButton: palette.Black.$700, // #D8D8D8
    // Background
    $backgroundPrimary: palette.Dark.$1000,
    $backgroundSecondary: palette.Dark.$900,
    // Line Separator
    $lineThin: palette.Dark.$700,
    $lineThick: palette.Black.$1000,
    // Status
    $success: palette.Green.$600,
    $disable: palette.Dark.$700,
    // OTHERS
    $hyperlink: palette.P2.$600,
    $alertWarning: '#FFDB7D',
    // Date Picker
    $datePickerSelected: palette.P2.$800,
    $datePickerSelectedText: palette.White.$700,
    $datePickerHighlightedBackground: '#544A3C30',
    $datePickerHighlightedText: palette.P2.$700,
    $datePickerDisabledText: palette.P2.$500,
    $datePickerText: palette.White.$700
  }
};

const rawLightTheme = {
  ...base,
  colors: colors.light
};

const light = makeTheme(rawLightTheme);

const dark = merge({}, light, {
  colors: colors.dark,
  linearGradients: {
    ...baseLinearGradients,
    disabledDefault: [palette.Dark.$700, palette.Dark.$700],
    disabledPressed: [palette.Dark.$700, palette.Dark.$700]
  }
});

const lightTheme = makeTheme({
  ...light,
  types: {
    reactNativeTypesOnly: true,
    onlyAllowThemeValues: {
      space: 'always'
    }
  }
});

const darkTheme: typeof light = {
  ...dark
};

export const theme = {
  light: lightTheme as DripsyFinalTheme,
  dark: darkTheme as DripsyFinalTheme
};
